import { AttackPattern, PatternState } from '../AttackPattern.js';

/**
 * Tracking Beam — exclusive to The Hollow Sovereign. Unlike Sweeping Laser
 * (which locks on once during the telegraph, then sweeps at a fixed rate
 * regardless of where the player goes), this beam continuously re-aims at
 * the player's *current* position for the whole active window, limited to
 * a slow turn rate — so it's dodgeable by moving perpendicular to it, but
 * punishes standing still or running straight down its length.
 *
 * `config` (turnRate/tickDamage/tickInterval/width/length) is per-boss
 * tuning, supplied by data/bosses.js.
 */
export class TrackingBeam extends AttackPattern {
  constructor({ minPhase = 1, config }) {
    super({ name: 'Tracking Beam', telegraphDuration: 0.8, activeDuration: 4.4, minPhase });
    this.config = config;
  }

  // How long after the attack starts (telegraph begin) before the beam
  // starts re-aiming — a brief held beat on the opening angle instead of
  // tracking from frame 1.
  static TRACKING_DELAY = 0.1;

  onBegin(boss, player) {
    this._angle = Math.atan2(player.y - boss.y, player.x - boss.x);
    this._elapsed = 0;
  }

  onTelegraphTick(dt, boss, player) {
    this._elapsed += dt;
    if (this._elapsed < TrackingBeam.TRACKING_DELAY) return;
    // Keeps re-aiming during the telegraph too, so the lock-on line is an
    // honest preview of where the beam will actually start tracking from.
    this._angle = Math.atan2(player.y - boss.y, player.x - boss.x);
  }

  // Because the beam keeps re-aiming right up through the end of the
  // telegraph (above), it's always pointed exactly at the player the
  // instant ACTIVE begins — dodging during the telegraph can't help, unlike
  // Sweeping Laser's lock-once-then-sweep-away. Without a grace tick here,
  // _tickTimer starting at 0 would land a hit on the very first ACTIVE
  // frame, before the player has any chance to react to it going live.
  onActiveStart() {
    this._tickTimer = this.config.tickInterval;
  }

  onActiveTick(dt, boss, player) {
    const { turnRate, tickDamage, tickInterval, width, length } = this.config;
    this._elapsed += dt;
    if (this._elapsed >= TrackingBeam.TRACKING_DELAY) {
      const desired = Math.atan2(player.y - boss.y, player.x - boss.x);
      let diff = desired - this._angle;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      const maxTurn = turnRate * dt;
      this._angle += Math.max(-maxTurn, Math.min(maxTurn, diff));
    }

    this._tickTimer -= dt;
    if (this._tickTimer <= 0 && this._playerInBeam(boss, player, width, length)) {
      player.takeDamage(tickDamage);
      this._tickTimer = tickInterval;
    }
  }

  _playerInBeam(boss, player, width, length) {
    const dx = player.x - boss.x;
    const dy = player.y - boss.y;
    const dist = Math.hypot(dx, dy);
    const pointAngle = Math.atan2(dy, dx);
    let diff = pointAngle - this._angle;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    const alongBeam = Math.cos(diff) * dist;
    const perpFromBeam = Math.abs(Math.sin(diff)) * dist;
    return alongBeam > 0 && alongBeam < length && perpFromBeam < width / 2 + player.radius;
  }

  getVisual(boss) {
    const { width, length } = this.config;
    if (this.state === PatternState.TELEGRAPH) {
      return { kind: 'laserAim', x: boss.x, y: boss.y, angle: this._angle, length, color: '#b87ae0' };
    }
    if (this.state === PatternState.ACTIVE) {
      return { kind: 'laserBeam', x: boss.x, y: boss.y, angle: this._angle, width, length, color: '#b87ae0' };
    }
    return null;
  }
}
